import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { oktaAuthentication, initiateOktaFlow, initiateOktaHubRedirectFlow } from '../../logic/store/User/AuthSlice';
import { useAppDispatch, useAppSelector } from '../../logic/store/Store';
import * as queryString from 'query-string';
import { FORCE_AUTH_QUERY_PARAM, POST_LOGIN_REDIRECT_KEY } from '../Routing/authRedirect';

/*
	Removes transient auth-routing query params from a redirect URL so they don't linger in the address bar after
 	the user is signed in.
*/
const stripTransientAuthParams = (rawUrl: string): string =>
{
	try
	{
		const url = new URL(rawUrl, window.location.origin);
		url.searchParams.delete(FORCE_AUTH_QUERY_PARAM);

		const search = url.searchParams.toString();
		return `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
	}
	catch
	{
		return rawUrl;
	}
};

/**
 * Handles the OAuth callback from Okta after user authentication.
 * Parses the authorization code from URL parameters and exchanges it for tokens.
 * Redirects to /dashboard on success or /login on failure.
 */
export const OktaHandler = (_props: {}) => 
{
	const dispatch = useAppDispatch();
	const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);
	const postLoginStatus = useAppSelector(state => state.ui.PostLoginProgress.status);
	const landingPage = useAppSelector(state => state.ui.PostLoginProgress.landingPage);
	const history = useHistory();

	useEffect(() => 
	{
		async function execute() 
		{
			try 
			{
				const parsed = queryString.parse(location.search);
				const errorValue = parsed.error;
				if(errorValue && typeof errorValue === 'string' && errorValue.length > 0)
				{
					history.push('/login?error=' + encodeURIComponent(`Login failed: ${parsed.error_description}`));
					return;
				}

				const code = parsed.code as string;
				if(code?.length)
				{
					const state = parsed.state as string;
					const authResult = await dispatch(oktaAuthentication({ code, state }));
					if(authResult && authResult.type === oktaAuthentication.rejected.type)
					{
						history.push('/login?error=' + encodeURIComponent(`Login failed: ${authResult.payload}`));
						return;
					}

					// Don't navigate here - we defer navigation until isAuthenticated state updates
					// This prevents race conditions with the authentication state propagation
				}
				else
				{
					await dispatch(initiateOktaHubRedirectFlow());
				}
			
			}
			catch (err) 
			{
				console.error('Okta authentication error:', err);
				history.push('/login?error=' + encodeURIComponent(err.message ?? 'An unknown error occurred'));
			}
		}
		execute();
	}, [dispatch, history]);

	useEffect(() => 
	{
		if(!isAuthenticated) return;

		// Wait for the PostLoginListener to finish resolving the initial
		// seller/grower/order so we redirect to the correct landing page
		if(postLoginStatus !== 'done' && postLoginStatus !== 'error') return;

		const redirectUrl = localStorage.getItem(POST_LOGIN_REDIRECT_KEY);
		if (redirectUrl)
		{
			localStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
			history.replace(stripTransientAuthParams(redirectUrl));
			return;
		}

		history.replace(landingPage ?? '/dashboard');
	}, [isAuthenticated, postLoginStatus, landingPage]);

	return <></>;
};
